import React, { useEffect, useCallback, useMemo, Suspense } from "react"
import { styles } from "./ExplanationModal.styles"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import useApi from "modules/library"
// import { isExplanationActivity } from "common/functions"
import {
  ACTIVITY_TYPES,
  FILE_TYPES,
  MODULE_TYPES,
} from "pages/student/activity/common/constants"
import Loading from "components/Loading"
import { useModuleContext } from "contexts/ModuleContext"
import Image from "components/Image"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import useTour from "hooks/useTour"
import {
  firstToolsGetStarted,
  secondToolsGetStarted,
} from "components/ActivityContent/Activity.GetStarted"

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const contents = {
  [ACTIVITY_TYPES.WORDS_TO_REMEMBER]: React.lazy(() =>
    import("components/ActivityContent/WordsToRemember")
  ),
  [ACTIVITY_TYPES.EXPLANATION]: React.lazy(() =>
    import("components/ActivityContent/Explanation")
  ),
  [ACTIVITY_TYPES.VIDEO_EXPLANATION]: React.lazy(() =>
    import("components/ActivityContent/Explanation")
  ),
}

const Activity = ({ activityTypeId, fontSettings }) => {
  const { loadingContent } = useModuleContext()
  if (loadingContent || activityTypeId === 0) {
    return <Loading />
  }

  if (contents[activityTypeId]) {
    const ContentComponent = contents[activityTypeId]
    return (
      <Suspense fallback={<Loading />}>
        <ContentComponent
          displayQuestionContent={false}
          teacherNotesOpen={false}
          viewMode
          fontSettings={fontSettings}
        />
      </Suspense>
    )
  } else {
    console.log(`Activity with id ${activityTypeId || "*"} not found`)
    return <Loading />
  }
}

export default function ExplanationModal({
  open,
  onClose,
  moduleId,
  title = "",
  showTour = false,
  exerciseExplanation = null,
  moduleTypeId,
  fontSettings = {
    fontSize: undefined,
    fontFamily: undefined,
    bgColor: undefined,
  },
}) {
  const {
    setActivities,
    activities,
    setExercise,
    exerciseIndex,
    setExerciseIndex,
    setActivity,
    activityIndex,
    changeActivityIndex,
  } = useModuleContext()
  const { getActivityContent, getExercises } = useApi()
  const {
    setIsOpen,
    // isGetStartedViewed,
    setSteps,
    setCurrentStep,
    setGetStartedViewed,
    superIsOpen,
  } = useTour()

  useEffect(() => {
    if (open && moduleId > 0) {
      loadExplanationContent()
        .catch(console.error)
        .then((explanationTypeId) => {
          console.log("~ showTour:", showTour, explanationTypeId)
          if (!showTour) return
          setGetStartedViewed("actions_tools_explanation_overview")
          if (explanationTypeId === ACTIVITY_TYPES.WORDS_TO_REMEMBER) {
            setSteps(firstToolsGetStarted({ setIsExplanationOpen: onClose }))
            setCurrentStep(2)
            setTimeout(() => {
              setSteps(firstToolsGetStarted({ setIsExplanationOpen: onClose }))
              setCurrentStep(2)
              superIsOpen(true)
              setGetStartedViewed("actions_tools_explanation_overview")
              // setGetStartedViewed(GET_STARTED.EXPLANATION_WTR)
            }, 1000)
          }
          if (explanationTypeId === ACTIVITY_TYPES.EXPLANATION) {
            setSteps(secondToolsGetStarted({ setIsExplanationOpen: onClose }))
            setCurrentStep(6)
            setTimeout(() => {
              setSteps(secondToolsGetStarted({ setIsExplanationOpen: onClose }))
              setCurrentStep(6)
              superIsOpen(true)
            }, 1000)
          }
        })
    } else {
      setActivities([])
      setExercise(null)
      changeActivityIndex(-1)
    }
  }, [open, moduleId])

  useEffect(() => {
    setIsOpen(false)
  }, [activityIndex])

  const loadExplanationContent = useCallback(async () => {
    if (
      exerciseExplanation !== null &&
      (exerciseExplanation?.files?.length > 0 ||
        exerciseExplanation?.text?.length > 0)
    ) {
      const activity = {
        activityTypeId: ACTIVITY_TYPES.EXPLANATION,
        activityId: -1,
      }
      const exercise = {
        ...exerciseExplanation,
        content: [exerciseExplanation],
      }
      activity.exercises = [exercise]
      setExercise(exercise || null)
      setActivity(activity)
      setActivities([activity])
      changeActivityIndex(0)
      setExerciseIndex(0)
      return ACTIVITY_TYPES.EXPLANATION
    }
    const response = await getActivityContent({ moduleId })
    if (response) {
      const activityContent = response?.data?.activities || []
      // get All explanation content
      const isVocabularyModule = moduleTypeId === MODULE_TYPES.VOCABULARY
      const isGrammarModule = moduleTypeId === MODULE_TYPES.GRAMMAR
      const hasExplanationContent =
        activityContent.filter(
          (item) => item.activityTypeId === ACTIVITY_TYPES.EXPLANATION
        ).length > 0
      const hasVideoContent =
        activityContent.filter(
          (item) => item.activityTypeId === ACTIVITY_TYPES.VIDEO_EXPLANATION
        ).length > 0
      const hasWordsToRememberContent =
        activityContent.filter(
          (item) => item.activityTypeId === ACTIVITY_TYPES.WORDS_TO_REMEMBER
        ).length > 0
      let explanationContent = activityContent.filter(
        (item) => item.activityTypeId === ACTIVITY_TYPES.EXPLANATION
      )
      if (
        isVocabularyModule &&
        (hasExplanationContent || hasVideoContent) &&
        hasWordsToRememberContent
      ) {
        // get words to remember content
        const wordsToRememberContent = activityContent.filter(
          (item) => item.activityTypeId === ACTIVITY_TYPES.WORDS_TO_REMEMBER
        )
        for (const key in wordsToRememberContent) {
          const activity = wordsToRememberContent[key]
          const results = await getExercises({
            moduleId,
            activityId: activity.activityId,
          })
          activity.exercises = results.sort((a, b) => a.sequence - b.sequence)
        }
        setExercise(wordsToRememberContent[0]?.exercises[0] || null)
        setActivities(wordsToRememberContent || [])
        changeActivityIndex(0)
        setExerciseIndex(0)
        return ACTIVITY_TYPES.WORDS_TO_REMEMBER
      }
      if (
        isGrammarModule &&
        (hasExplanationContent || hasVideoContent) &&
        hasWordsToRememberContent
      ) {
        // for each explinationActivity get exercises
        const videoContent = []
        const imageContent = []
        for (const key in explanationContent) {
          const activity = explanationContent[key]
          // console.log("🚀 ~ file: LoadingActivities.js ~ line 529 ~ showExplination ~ activity", activity)
          const results = await getExercises({
            moduleId,
            activityId: activity.activityId,
          })
          activity.exercises = results.sort((a, b) => a.sequence - b.sequence)
          // if there is an exercise with content video
          const exercises = activity.exercises
          for (const vKey in exercises) {
            const contentExercise = exercises[vKey]?.content[0]
            const contentVideo = contentExercise.files.filter(
              (item) => item.fileTypeId === FILE_TYPES.VIDEO
            )
            const contentImage = contentExercise.files.filter(
              (item) => item.fileTypeId === FILE_TYPES.IMAGE
            )
            if (contentVideo.length > 0) {
              videoContent.push(activity.activityId)
            }
            if (contentImage.length > 0) {
              imageContent.push(activity.activityId)
            }
          }
        }
        // if there activties with video and images, leave only the images
        if (videoContent.length > 0 && imageContent.length > 0) {
          explanationContent = explanationContent.filter(
            (item) => !videoContent.includes(item.activityId)
          )
        }
        setExercise(explanationContent[0]?.exercises[0] || null)
        setActivity(explanationContent[0])
        setActivities(explanationContent || [])
        changeActivityIndex(0)
        setExerciseIndex(0)
        return explanationContent[0]?.activityTypeId
      }
      if (explanationContent.length > 0) {
        // for each explinationActivity get exercises
        const videoContent = []
        const imageContent = []
        for (const key in explanationContent) {
          const activity = explanationContent[key]
          // console.log("🚀 ~ file: LoadingActivities.js ~ line 529 ~ showExplination ~ activity", activity)
          const results = await getExercises({
            moduleId,
            activityId: activity.activityId,
          })
          activity.exercises = results.sort((a, b) => a.sequence - b.sequence)
          // if there is an exercise with content video
          const exercises = activity.exercises
          for (const vKey in exercises) {
            const contentExercise = exercises[vKey]?.content[0]
            const contentVideo = contentExercise.files.filter(
              (item) => item.fileTypeId === FILE_TYPES.VIDEO
            )
            const contentImage = contentExercise.files.filter(
              (item) => item.fileTypeId === FILE_TYPES.IMAGE
            )
            if (contentVideo.length > 0) {
              videoContent.push(activity.activityId)
            }
            if (contentImage.length > 0) {
              imageContent.push(activity.activityId)
            }
          }
        }
        // if there activties with video and images, leave only the images
        if (videoContent.length > 0 && imageContent.length > 0) {
          explanationContent = explanationContent.filter(
            (item) => !videoContent.includes(item.activityId)
          )
        }
        setExercise(explanationContent[0]?.exercises[0] || null)
        setActivity(explanationContent[0])
        setActivities(explanationContent || [])
        changeActivityIndex(0)
        setExerciseIndex(0)
        return explanationContent[0]?.activityTypeId
      } else {
        // get words to remember content
        const wordsToRememberContent = activityContent.filter(
          (item) => item.activityTypeId === ACTIVITY_TYPES.WORDS_TO_REMEMBER
        )
        if (wordsToRememberContent.length > 0) {
          for (const key in wordsToRememberContent) {
            const activity = wordsToRememberContent[key]
            const results = await getExercises({
              moduleId,
              activityId: activity.activityId,
            })
            activity.exercises = results.sort((a, b) => a.sequence - b.sequence)
          }
          setExercise(wordsToRememberContent[0]?.exercises[0] || null)
          setActivities(wordsToRememberContent || [])
          changeActivityIndex(0)
          setExerciseIndex(0)
          return ACTIVITY_TYPES.WORDS_TO_REMEMBER
        } else {
          // video content
          explanationContent = activityContent.filter(
            (item) => item.activityTypeId === ACTIVITY_TYPES.VIDEO_EXPLANATION
          )
          const activity = explanationContent[0]
          if (!activity) {
            return -1
          }
          // console.log("🚀 ~ file: LoadingActivities.js ~ line 529 ~ showExplination ~ activity", activity)
          const results = await getExercises({
            moduleId,
            activityId: activity.activityId,
          })
          activity.exercises = results.sort((a, b) => a.sequence - b.sequence)
          // if there is an exercise with content video
          const exercises = activity.exercises || []
          setExercise(exercises[0] || null)
          setActivity(explanationContent[0])
          setActivities(explanationContent || [])
          changeActivityIndex(0)
          setExerciseIndex(0)
          return explanationContent[0]?.activityTypeId
        }
      }
    }
    return -1
  }, [moduleId, exerciseExplanation])

  const onArrowBack = useCallback(
    (e) => {
      e.stopPropagation()
      const prevIndex = exerciseIndex - 1
      if (prevIndex >= 0) {
        const _activity = activities[activityIndex]
        if (!_activity) return
        const _exercise = _activity?.exercises[prevIndex]
        setExercise(_exercise)
        setExerciseIndex((prev) => prev - 1)
      } else {
        if (activityIndex > 0) {
          const _activity = activities[activityIndex - 1]
          const lastIndex = (_activity?.exercises?.length || 1) - 1
          const _exercise = _activity?.exercises[lastIndex]
          setExercise(_exercise)
          setActivity(_activity)
          changeActivityIndex(activityIndex - 1)
          setExerciseIndex(lastIndex)
        }
      }
    },
    [
      activities,
      activityIndex,
      exerciseIndex,
      setExercise,
      setExerciseIndex,
      setActivity,
      changeActivityIndex,
    ]
  )

  const onArrowNext = useCallback(
    (e) => {
      e.stopPropagation()
      const nextIndex = exerciseIndex + 1
      if (nextIndex < activities[activityIndex]?.exercises?.length) {
        const _activity = activities[activityIndex]
        const _exercise = _activity.exercises[nextIndex]
        setExercise(_exercise)
        setExerciseIndex((prev) => prev + 1)
      } else {
        if (activityIndex < activities.length - 1) {
          const _activity = activities[activityIndex + 1]
          const _exercise = _activity.exercises[0]
          setExercise(_exercise)
          setActivity(_activity)
          changeActivityIndex(activityIndex + 1)
          setExerciseIndex(0)
        }
      }
    },
    [
      activities,
      activityIndex,
      exerciseIndex,
      setExercise,
      setExerciseIndex,
      setActivity,
      changeActivityIndex,
    ]
  )

  const totalExercises = useMemo(() => {
    return (activities || []).reduce(
      (acc, act) => acc + (act?.exercises?.length || 0),
      0
    )
  }, [activities])

  const currentGlobalExerciseNumber = useMemo(() => {
    if (!activities || activities.length === 0 || activityIndex < 0) return 1
    const previousExercisesCount = activities
      .slice(0, activityIndex)
      .reduce((acc, act) => acc + (act?.exercises?.length || 0), 0)
    return previousExercisesCount + exerciseIndex + 1
  }, [activities, activityIndex, exerciseIndex])

  const isPrevDisabled = activityIndex <= 0 && exerciseIndex <= 0
  const isNextDisabled =
    activityIndex >= (activities?.length || 1) - 1 &&
    exerciseIndex >=
    (activities?.[activityIndex]?.exercises?.length || 1) - 1

  const close = useCallback(() => {
    setIsOpen(false)
    onClose()
  }, [setIsOpen])

  return (
    <Dialog
      open={open}
      onClose={close}
      TransitionComponent={Transition}
      fullWidth
      PaperProps={{
        sx: [
          styles.container,
          exerciseExplanation !== null &&
          (exerciseExplanation?.files?.length > 0 ||
            exerciseExplanation?.text?.length > 0) &&
          styles.containerExerciseExplanation,
        ],
      }}
    >
      <IconButton
        aria-label="close"
        onClick={(e) => {
          e.stopPropagation()
          close()
        }}
        sx={styles.closeButton}
        className="close-explanation"
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      {title !== "" && (
        <DialogTitle sx={styles.title} component={"div"}>
          <Image
            flex={1 / 3}
            name={"searchIcon"}
            wrapperClassName="icon"
            width={35}
            height={35}
            wrapperStyle={{
              flex: 1 / 3,
              justifyContent: "flex-start",
            }}
            style={{
              width: "auto",
            }}
          />
          <Typography
            flex={1}
            variant="h2"
            textAlign={"center"}
            sx={[
              { lineHeight: "1.4285em" },
              fontSettings?.fontFamily && {
                fontFamily: fontSettings?.fontFamily,
              },
            ]}
          >
            {title}
          </Typography>
          <Box flex={1 / 3} />
        </DialogTitle>
      )}
      <DialogContent
        sx={[
          styles.content,
          exerciseExplanation?.files?.length === 0 &&
          exerciseExplanation?.text?.length > 0 &&
          styles.contentTextOnly,
        ]}
      >
        {activityIndex >= 0 && (
          <Activity
            key={`explanation-${activities[activityIndex]?.exercises?.[exerciseIndex]?.exerciseId}`}
            activityTypeId={activities[activityIndex]?.activityTypeId}
            fontSettings={fontSettings}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" alignItems="center" justifyContent="center">
          <IconButton
            size="small"
            sx={styles.arrowButton}
            onClick={onArrowBack}
            disabled={isPrevDisabled}
          >
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={[
              styles.arrowsText,
              fontSettings?.fontFamily && {
                fontFamily: fontSettings?.fontFamily,
              },
            ]}
          >
            {currentGlobalExerciseNumber} / {totalExercises || 0}
          </Typography>
          <IconButton
            size="small"
            sx={styles.arrowButton}
            onClick={onArrowNext}
            disabled={isNextDisabled}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
